import {
    addISODateDays,
    addJalaliMonths,
    formatJalaliDate,
    formatJalaliDateTime,
    formatJalaliMonth,
    fromJalaliParts,
    isoToJalali,
    jalaliToISO,
    jalaliWeekdayIndex,
    jalaaliMonthDays,
    parseJalaliDate,
    toJalaliParts,
    toPersianDigits,
} from '@/lib/format';

export {
    addISODateDays,
    addJalaliMonths,
    formatJalaliDate,
    formatJalaliDateTime,
    formatJalaliMonth,
    fromJalaliParts,
    isoToJalali,
    jalaliToISO,
    jalaliWeekdayIndex,
    jalaaliMonthDays,
    parseJalaliDate,
    toJalaliParts,
};

/**
 * Number, money and percentage formatting for reports.
 *
 * The report API serialises money as a JSON string (`"126424000.0000"`, see
 * `docs/domains/reporting.md`), so every formatter here coerces its input with
 * `toNumber` before touching it. Pass any raw API value straight in.
 */

const FA_DIGITS = new Intl.NumberFormat('fa-IR');

/** Coerce API values (number, numeric string, null) into a finite number. */
export const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/** `1234567` -> `۱٬۲۳۴٬۵۶۷` */
export const formatNumber = (value) => FA_DIGITS.format(Math.round(toNumber(value)));

/**
 * Swap Latin digits inside an already-built label for Persian ones, leaving
 * every other character alone: `1405/05/22` -> `۱۴۰۵/۰۵/۲۲`.
 *
 * Bucket labels arrive from the API pre-formatted (`"1405/05/22"`, `"خرداد 1405"`),
 * so they only need the digit script corrected, not a full re-format.
 */
export const persianDigits = toPersianDigits;

/** `1234567` -> `۱٫۲۳` (keeps up to `digits` decimals) */
export const formatDecimal = (value, digits = 2) =>
    new Intl.NumberFormat('fa-IR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    }).format(toNumber(value));

/**
 * Compact money for chart axes and tight tiles:
 * `3700000` -> `۳٫۷ میلیون`, `2500000000` -> `۲٫۵ میلیارد`.
 */
export const formatCompact = (value) => {
    const amount = toNumber(value);
    const abs = Math.abs(amount);
    if (abs >= 1_000_000_000) return `${formatDecimal(amount / 1_000_000_000, 1)} میلیارد`;
    if (abs >= 1_000_000) return `${formatDecimal(amount / 1_000_000, 1)} میلیون`;
    if (abs >= 1_000) return `${formatDecimal(amount / 1_000, 0)} هزار`;
    return formatNumber(amount);
};

/** Full money value with the unit, for tables and tiles. */
export const formatToman = (value) => `${formatNumber(value)} تومان`;

/** Compact money value with the unit, for chart axes. */
export const formatCompactToman = (value) => `${formatCompact(value)} تومان`;

/** `12.3456` -> `۱۲٫۳٪` */
export const formatPercent = (value, digits = 1) =>
    `${new Intl.NumberFormat('fa-IR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    }).format(toNumber(value))}٪`;

/**
 * Signed percentage for period-over-period deltas. Returns null when the
 * backend could not compute growth (previous window was empty), so callers can
 * render "—" instead of a fake 0%.
 */
export const formatDelta = (value) => {
    if (value === null || value === undefined) return null;
    const amount = toNumber(value);
    const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
    return `${sign}${formatPercent(Math.abs(amount))}`;
};

/** Direction of a delta, used to pick a colour. */
export const deltaTone = (value) => {
    if (value === null || value === undefined) return 'flat';
    const amount = toNumber(value);
    if (amount > 0) return 'up';
    if (amount < 0) return 'down';
    return 'flat';
};

/** `2026-09-22` -> `۱۴۰۵/۰۶/۳۱` (alias used in range captions) */
export const formatRangeCaption = (dateFrom, dateTo) =>
    `${formatJalaliDate(dateFrom)} تا ${formatJalaliDate(dateTo)}`;

/**
 * Recharts-friendly axis tick. Falls back to the raw value when a bucket label
 * is already short (e.g. an hour label such as `۱۲`).
 */
export const axisTick = (value) => formatCompact(value);

/** Axis tick for pre-formatted bucket labels — corrects the digit script only. */
export const categoryAxisTick = (value) => persianDigits(value);

/** `14:00` style label for the hour-of-day report. */
export const formatHourLabel = (hour) => `${formatNumber(hour)}:۰۰`;

/**
 * Human label for the resolved granularity, shown next to the range caption.
 */
export const GRANULARITY_LABEL = {
    day: 'روزانه',
    week: 'هفتگی',
    month: 'ماهانه',
};
